import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useCreateProject } from '../../../api/projects';
import { useReferenceLists } from '../../../api/referenceLists';
import { PageMeta } from '../shared/PageMeta';

interface FormValues {
  title: string;
  description: string;
  adaptationZone: string;
  deliveryAddress: string;
  deliveryCountry: string;
  neededBy: string;
  links: string;
  mediaConsent: boolean;
}

export function CreateProjectPage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const { data: refLists } = useReferenceLists();
  const [step, setStep] = useState(1);

  const { register, handleSubmit, trigger, formState: { errors } } = useForm<FormValues>({
    defaultValues: { mediaConsent: false },
  });

  const nextStep = async () => {
    const valid = await trigger(['title', 'description', 'deliveryAddress', 'deliveryCountry']);
    if (valid) setStep(2);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const links = data.links ? data.links.split('\n').map((l) => l.trim()).filter(Boolean) : [];
      const project = await createProject.mutateAsync({
        title: data.title,
        description: data.description,
        adaptationZone: data.adaptationZone || undefined,
        deliveryAddress: data.deliveryAddress,
        deliveryCountry: data.deliveryCountry,
        neededBy: data.neededBy || undefined,
        mediaConsent: data.mediaConsent,
        links,
      });
      toast.success('Project created as draft. Add asks and media, then publish.');
      navigate(`/marketplace/projects/${project.id}`);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to create project');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <PageMeta title="Create Project" description="Create a new seed donation project" path="/marketplace/projects/new" />

      <Link to="/marketplace/dashboard" className="text-sm text-spi-green hover:underline mb-4 inline-block">&larr; Back to dashboard</Link>
      <h2 className="text-3xl mb-6">Create Project</h2>

      <div className="flex gap-2 mb-6">
        <div className={`flex-1 h-1 rounded-full ${step >= 1 ? 'bg-spi-green' : 'bg-spi-parchment'}`} />
        <div className={`flex-1 h-1 rounded-full ${step >= 2 ? 'bg-spi-green' : 'bg-spi-parchment'}`} />
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {step === 1 && (
          <div className="card p-8 space-y-4">
            <h3 className="text-lg font-semibold mb-2">Project Information</h3>

            <div>
              <label className="input-label">Title *</label>
              <input {...register('title', { required: 'Title is required', maxLength: 200 })} className="input" placeholder="e.g., Seeds for Nakuru County Schools" />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
            </div>

            <div>
              <label className="input-label">Description *</label>
              <textarea {...register('description', { required: 'Description is required' })} className="input min-h-[120px]" placeholder="Describe your project, its goals, and the communities it serves..." />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">Delivery Address *</label>
                <input {...register('deliveryAddress', { required: 'Address is required' })} className="input" />
                {errors.deliveryAddress && <p className="text-red-500 text-xs mt-1">{errors.deliveryAddress.message}</p>}
              </div>
              <div>
                <label className="input-label">Delivery Country *</label>
                <input {...register('deliveryCountry', { required: 'Country is required', minLength: 2 })} className="input" placeholder="e.g., Uganda" />
                {errors.deliveryCountry && <p className="text-red-500 text-xs mt-1">{errors.deliveryCountry.message}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">USDA Adaptation Zone</label>
                <select {...register('adaptationZone')} className="input">
                  <option value="">Select zone (optional)</option>
                  {refLists?.zone?.map((item) => (
                    <option key={item.id} value={item.value}>Zone {item.value}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">Needed By</label>
                <input type="date" {...register('neededBy')} className="input" />
              </div>
            </div>

            <div>
              <label className="input-label">Links (one per line)</label>
              <textarea {...register('links')} className="input min-h-[80px]" placeholder="https://example.org/our-project" />
            </div>

            <button type="button" onClick={nextStep} className="btn btn-primary w-full">
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="card p-8 space-y-4">
            <h3 className="text-lg font-semibold mb-2">Media Consent</h3>

            <div className="bg-spi-seed rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" {...register('mediaConsent', { required: 'You must agree to the media consent' })} className="mt-1" />
                <span className="text-sm text-spi-charcoal">
                  I grant permission for images and content uploaded to this project to be used by SPI and Bay Crew Tech, LLC for communications, marketing, and promotional purposes. See our{' '}
                  <Link to="/marketplace/terms" target="_blank" className="text-spi-green hover:underline">Terms of Service</Link> and{' '}
                  <Link to="/marketplace/privacy" target="_blank" className="text-spi-green hover:underline">Privacy Policy</Link>.
                </span>
              </label>
              {errors.mediaConsent && <p className="text-red-500 text-xs mt-1">{errors.mediaConsent.message}</p>}
            </div>

            <p className="text-sm text-spi-slate">
              Your project will be created as a <strong>draft</strong>. You can add seed requests (asks) and upload media before publishing.
            </p>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn btn-outline flex-1">
                Back
              </button>
              <button type="submit" disabled={createProject.isPending} className="btn btn-primary flex-1">
                {createProject.isPending ? 'Creating...' : 'Create Draft'}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
